import { useState, type FormEvent } from "react";
import { Wand2, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { MarwoodLogoAnimated } from "@jaredmarwood/marwoodui";
import { PreloadLink as Link } from "./PreloadLink";
import { api } from "@/lib/api";

interface FooterProps {
  compact?: boolean;
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setErrorMsg("");

    try {
      await api.post("/newsletter/subscribe", { email: email.trim() });
      setStatus("success");
      setEmail("");
    } catch (err: unknown) {
      setStatus("error");
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrorMsg(msg || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-400">
        <CheckCircle2 className="h-4 w-4" />
        <span>You're subscribed! Check your inbox.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full pl-9 pr-3 py-2 text-sm bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 placeholder:text-muted-foreground/50 transition-all"
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading"}
          className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
        >
          {status === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Subscribe"
          )}
        </button>
      </div>
      {status === "error" && (
        <p className="text-xs text-red-400">{errorMsg}</p>
      )}
    </form>
  );
}

export function Footer({ compact = false }: FooterProps) {
  const currentYear = new Date().getFullYear();

  if (compact) {
    return (
      <footer className="border-t bg-card/30 backdrop-blur-sm mt-auto">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-1.5">
                <div className="h-5 w-5 rounded bg-gift-gradient flex items-center justify-center">
                  <Wand2 className="h-3 w-3 text-white" />
                </div>
                <span className="text-sm font-semibold gradient-text">Prezntz</span>
              </Link>
              <span className="text-muted-foreground/40 hidden sm:inline">|</span>
              <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
                <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
                <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
                <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-xs text-muted-foreground">
                &copy; {currentYear}{" "}
                <a href="https://marwood.au" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                  Marwood X
                </a>
              </p>
              <span className="text-xs text-muted-foreground/40">
                v{import.meta.env.VITE_APP_VERSION || "1.0.0"}
              </span>
              <MarwoodLogoAnimated href="https://marwood.au" size={48} markColor="#7c3aed" />
            </div>
          </div>
          <p className="mt-3 text-center text-[10px] text-muted-foreground/50">
            As an Amazon Associate, Prezntz earns from qualifying purchases.{" "}
            <Link to="/affiliate-disclosure" className="hover:text-muted-foreground transition-colors underline">
              Disclosure
            </Link>
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t bg-card/30 backdrop-blur-sm">
      <div className="container mx-auto px-4 py-12">
        {/* Newsletter Section */}
        <div className="mb-10 pb-10 border-b border-border/30">
          <div className="max-w-xl mx-auto text-center space-y-4">
            <h3 className="text-lg font-semibold">Get Gift Ideas in Your Inbox</h3>
            <p className="text-sm text-muted-foreground">
              Curated recommendations and insider tips for every occasion. No spam, unsubscribe anytime.
            </p>
            <div className="max-w-sm mx-auto">
              <NewsletterForm />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gift-gradient flex items-center justify-center">
                <Wand2 className="h-4 w-4 text-white" />
              </div>
              <span className="text-lg font-bold gradient-text">Prezntz</span>
            </Link>
            <p className="text-sm text-muted-foreground">
              Smart gift recommendations for every person and occasion.
              Never give a bad gift again.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Product</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/wizard"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Gift Wizard
                </Link>
              </li>
              <li>
                <Link
                  to="/subscription"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/cards"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Greeting Cards
                </Link>
              </li>
              <li>
                <Link
                  to="/gift-exchange/create"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Gift Exchange
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Company</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/affiliate-disclosure"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Affiliate Disclosure
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Occasions */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Gift Ideas</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/gifts-for/birthday-gifts-for-mom"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Birthday Gifts for Mom
                </Link>
              </li>
              <li>
                <Link
                  to="/gifts-for/christmas-gifts-for-wife"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Christmas Gifts
                </Link>
              </li>
              <li>
                <Link
                  to="/gifts-for/anniversary-gifts"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Anniversary Gifts
                </Link>
              </li>
              <li>
                <Link
                  to="/gifts-for/wedding-gifts"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Wedding Gifts
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            &copy; {currentYear} <a href="https://marwood.au" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Marwood X</a>. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-xs text-muted-foreground/60">
              v{import.meta.env.VITE_APP_VERSION || "1.0.0"}
            </span>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <MarwoodLogoAnimated href="https://marwood.au" size={56} />
            </div>
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground/60 text-center">
          As an Amazon Associate, Prezntz earns from qualifying purchases.{" "}
          <Link to="/affiliate-disclosure" className="hover:text-muted-foreground transition-colors underline">
            Affiliate Disclosure
          </Link>
        </p>
      </div>
    </footer>
  );
}
